import dynamic from 'next/dynamic';
import React, { Suspense, useState } from 'react';
import {
  getBreadCrumbSchema,
  TAB_GOLD_SILVER,
} from '../../../constants/stocks/componentStaticData';
import { handleTrack } from '../../../utils/tracker';
import { Breadcrumb, BREADCRUMB_TYPE } from '../../Atoms/BreadCrumb';
import { TopCTA } from '../../cms/layout/components';
import { Table } from '../../Molecules/table';
import { GoldChart } from '../common/Chart';
import {
  CaratTableSection,
  CARAT_TABLE_CONFIG,
} from '../common/GoldTable/CaratTableSection';
import { LAST_10_DAYS_GOLD_RATE_COLUMN } from '../common/GoldTable/GoldColumns';
import { LinkTabs } from '../common/LinkedTabs';
import { GoldRateCard } from '../common/RateCard/GoldRateCard';
import { Sidebar } from '../common/Sidebar';
import { CaratChange, GOLD_CITY_HOMEPAGE_PROPS } from '../common/types';
import { capitalized, getGramWiseGoldCaratData } from '../common/utils';
import { CityComparisonTable } from './CityComparisonTable';
import { GoldCalculator } from './GoldCalculator';
import { GoldSilverContent } from '../common/GoldSilverContent';
import { GoldSilverLayout } from '../common/GoldSilverContent/styles';

const FaqComponent = dynamic(
  () =>
    import(/* webpackChunkName: "faq-component" */ '../../Common/FAQS').then(
      (mod) => mod.FaqComponent
    ),
  { suspense: true }
);

const GoldCityHomepage = ({
  goldData,
  cityData,
  city,
  graphData,
  metal,
  top_cta,
  cmsContent,
  cmsFaq,
  cmsTitle,
}: GOLD_CITY_HOMEPAGE_PROPS): JSX.Element => {
  const last10DaysColumns = LAST_10_DAYS_GOLD_RATE_COLUMN(10);

  const capitalizedCityName = capitalized(city.name);
  const [change, setChange] = useState<CaratChange>({
    change22k: 0,
    change24k: 0,
  });
  const [percentageChange, setPercentageChange] = useState<CaratChange>({
    change22k: 0,
    change24k: 0,
  });

  const captureEvent = (e: React.MouseEvent<Element>) => {
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };
  const BREADCRUMB: Array<BREADCRUMB_TYPE> = [
    { title: 'Home', link: '/' },
    { title: 'Gold Rate Today', link: `/${metal}-rate-india` },
    { title: `Gold rate in ${capitalizedCityName}`, link: null },
  ];

  return (
    <div className="mt-[87px] sm:mt-0 sm:px-4 ">
      <LinkTabs
        buttonData={TAB_GOLD_SILVER}
        activeValue={metal}
        city={city.name}
      />

      <div className="mt-16 sm:mt-4">
        <TopCTA CTA={top_cta || ''} onClickCB={captureEvent} />
      </div>
      <div className="pt-4 wg-container">
        <div className="py-4">
          <Breadcrumb dataSet={BREADCRUMB} />
        </div>

        <div className="flex flex-row-reverse gap-10">
          <div className="w-1/4 sm:hidden">
            <Sidebar city={city.name} cityData={cityData} metal={metal} />
          </div>
          <div className="w-3/4 sm:w-full">
            <section className="pb-2 border border-gray-600 rounded-lg p-2">
              <GoldRateCard
                data={goldData.today}
                cities={[
                  ...cityData.country,
                  ...cityData.major_city,
                  ...cityData.metro_city,
                ]}
                selectedCity={city.name}
                cmsTitle={cmsTitle}
                change={change}
                percentageChange={percentageChange}
              />
            </section>

            <section className="my-2">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                Gold Price Chart and Trend in {capitalizedCityName}
              </h2>

              <div className="py-6">
                <GoldChart
                  data={graphData}
                  city={city.name}
                  setChange={setChange}
                  setPercentageChange={setPercentageChange}
                />
              </div>
            </section>

            <section className="my-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                Gold Calculator
              </h2>
              <div className="py-6">
                <GoldCalculator data={goldData.today} />
              </div>
            </section>

            {CARAT_TABLE_CONFIG.map(({ carat, label }) => (
              <CaratTableSection
                key={carat}
                label={label}
                carat={carat}
                cityName={capitalizedCityName}
                data={getGramWiseGoldCaratData(goldData.today, carat)}
              />
            ))}

            <section className="my-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                Gold Rate in {capitalizedCityName} for Last 10 Days (per 10 g)
              </h2>
              <div className="py-6">
                <Table
                  columns={last10DaysColumns}
                  tableId={'testTable2'}
                  data={goldData.last_ten_days_data}
                  viewMoreToggle={false}
                />
              </div>
            </section>

            <GoldSilverContent cmsContent={cmsContent} />

            <section className="my-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                Gold Rate Today in Major Indian Cities
              </h2>
              <div className="py-6">
                <CityComparisonTable
                  citiesData={goldData.major_cities_data || []}
                  metroCities={cityData.metro_city}
                  city={city.name}
                />
              </div>
            </section>

            {cmsFaq && cmsFaq.length > 0 && (
              <GoldSilverLayout>
                <Suspense fallback={<div>Loading FAQs...</div>}>
                  <FaqComponent
                    FAQS={cmsFaq}
                    isShowSchema={true}
                    variant="partner"
                    faqIconColor={'#314259'}
                    htmlFromApi={true}
                    expanded={'false'}
                  />
                </Suspense>
              </GoldSilverLayout>
            )}
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getBreadCrumbSchema(BREADCRUMB)),
            }}
          ></script>
        </div>
      </div>
    </div>
  );
};

export default GoldCityHomepage;
